import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table, Button, Space, Tag, Typography, Input, message, Modal, Form,
  InputNumber, Select, Switch, Popconfirm, Card, Upload, Divider, Row, Col, Tabs,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  StarOutlined, StarFilled, UploadOutlined, EyeOutlined,
} from '@ant-design/icons';
import { productsAPI, categoriesAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

function ProductListPage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const pageSize = 20;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productsAPI.getAll({ page, page_size: pageSize, search });
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error(t('products.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await categoriesAPI.getAll();
      setCategories(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => {
    setEditingProduct(null);
    form.resetFields();
    form.setFieldsValue({ currency: 'TWD', stock: 0, is_active: true, is_featured: false });
    setModalOpen(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    form.setFieldsValue({
      name: product.name,
      short_description: product.short_description,
      description: product.description,
      name_th: product.name_th,
      short_description_th: product.short_description_th,
      description_th: product.description_th,
      price: product.price,
      currency: product.currency || 'TWD',
      sku: product.sku,
      stock: product.stock || 0,
      category_id: product.category_id,
      organization: product.organization,
      contact_person: product.contact_person,
      contact_email: product.contact_email,
      contact_phone: product.contact_phone,
      source_url: product.source_url,
      thumbnail: product.thumbnail,
      is_active: product.is_active,
      is_featured: product.is_featured,
      tags: product.tags,
      features: product.features ? product.features.join('\n') : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = { ...values };
      if (typeof payload.features === 'string') {
        payload.features = payload.features.split('\n').map(s => s.trim()).filter(Boolean);
      }

      if (editingProduct) {
        await productsAPI.update(editingProduct.id, payload);
        message.success(t('products.updateSuccess'));
      } else {
        await productsAPI.create(payload);
        message.success(t('products.createSuccess'));
      }
      setModalOpen(false);
      form.resetFields();
      fetchProducts();
    } catch (err) {
      if (err.errorFields) {
        message.error(t('common.fillRequired'));
      } else {
        message.error(`${t('common.saveFailed')}: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await productsAPI.delete(id);
      message.success(t('products.deleteSuccess'));
      fetchProducts();
    } catch {
      message.error(t('common.deleteFailed'));
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      await productsAPI.update(product.id, { is_featured: !product.is_featured });
      fetchProducts();
    } catch {
      message.error(t('common.updateFailed'));
    }
  };

  const handleToggleActive = async (product, checked) => {
    try {
      await productsAPI.update(product.id, { is_active: checked });
      fetchProducts();
    } catch {
      message.error(t('common.updateFailed'));
    }
  };

  const columns = [
    {
      title: t('products.table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 280,
      render: (text, record) => (
        <Space>
          {record.thumbnail && (
            <img src={record.thumbnail} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            {record.sku && <Text type="secondary" style={{ fontSize: 12 }}>SKU: {record.sku}</Text>}
          </div>
        </Space>
      ),
    },
    {
      title: t('products.table.organization'),
      dataIndex: 'organization',
      key: 'organization',
      width: 120,
      render: v => v ? <Tag>{v}</Tag> : '-',
    },
    {
      title: t('products.table.price'),
      dataIndex: 'price',
      key: 'price',
      width: 120,
      align: 'right',
      render: (v, record) => v ? `${record.currency || 'TWD'} ${v.toLocaleString()}` : '-',
    },
    {
      title: t('products.table.stock'),
      dataIndex: 'stock',
      key: 'stock',
      width: 80,
      align: 'center',
    },
    {
      title: t('products.table.featured'),
      key: 'is_featured',
      width: 60,
      align: 'center',
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={record.is_featured ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
          onClick={() => handleToggleFeatured(record)}
        />
      ),
    },
    {
      title: t('products.table.active'),
      key: 'is_active',
      width: 70,
      align: 'center',
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.is_active}
          onChange={(checked) => handleToggleActive(record, checked)}
        />
      ),
    },
    {
      title: '語系',
      key: 'langs',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Space size={2}>
          <span>🇹🇼</span>
          {record.name_th && <span>🇹🇭</span>}
        </Space>
      ),
    },
    {
      title: t('products.table.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title={t('common.confirmDelete')} onConfirm={() => handleDelete(record.id)} okText={t('common.delete')} cancelText={t('common.cancel')}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>{t('products.pageTitle')}</Title>
        <Space>
          <Input.Search
            placeholder={t('products.searchPlaceholder')}
            allowClear
            onSearch={v => { setSearch(v); setPage(1); }}
            style={{ width: 260 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('products.addButton')}
          </Button>
        </Space>
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p) => setPage(p),
            showTotal: (total) => t('products.paginationTotal', { total }),
          }}
          size="middle"
        />
      </Card>

      <Modal
        title={editingProduct ? t('products.editTitle') : t('products.addTitle')}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={720}
        confirmLoading={saving}
        styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical">
          <Divider orientation="left" plain>{t('products.form.basicInfo')}</Divider>

          <Tabs defaultActiveKey="zh" items={[
            { key: 'zh', label: t('products.form.langZh'), children: (
              <>
                <Form.Item name="name" label={t('products.form.name')} rules={[{ required: true, message: t('products.form.nameRequired') }]}>
                  <Input placeholder={t('products.form.namePlaceholder')} />
                </Form.Item>
                <Form.Item name="short_description" label={t('products.form.shortDesc')}>
                  <Input placeholder={t('products.form.shortDescPlaceholder')} maxLength={200} />
                </Form.Item>
                <Form.Item name="description" label={t('products.form.description')}>
                  <TextArea rows={4} placeholder={t('products.form.descriptionPlaceholder')} />
                </Form.Item>
              </>
            )},
            { key: 'th', label: t('products.form.langTh'), children: (
              <>
                <Form.Item name="name_th" label={t('products.form.nameTh')}>
                  <Input placeholder={t('products.form.nameThPlaceholder')} />
                </Form.Item>
                <Form.Item name="short_description_th" label={t('products.form.shortDescTh')}>
                  <Input placeholder={t('products.form.shortDescThPlaceholder')} maxLength={200} />
                </Form.Item>
                <Form.Item name="description_th" label={t('products.form.descriptionTh')}>
                  <TextArea rows={4} placeholder={t('products.form.descriptionThPlaceholder')} />
                </Form.Item>
              </>
            )},
          ]} />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="organization" label={t('products.form.organization')}>
                <Input placeholder={t('products.form.organizationPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sku" label={t('products.form.sku')}>
                <Input placeholder={t('products.form.skuPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category_id" label={t('products.form.category')}>
                <Select placeholder={t('products.form.categoryPlaceholder')} allowClear>
                  {categories.map(c => (
                    <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tags" label={t('products.form.tags')}>
                <Select mode="tags" placeholder={t('products.form.tagsPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>{t('products.form.priceStock')}</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="price" label={t('products.form.price')}>
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currency" label={t('products.form.currency')}>
                <Select>
                  <Select.Option value="TWD">{t('products.form.currencyTWD')}</Select.Option>
                  <Select.Option value="USD">{t('products.form.currencyUSD')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="stock" label={t('products.form.stock')}>
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>{t('products.form.productInfo')}</Divider>

          <Form.Item name="features" label={t('products.form.features')}>
            <TextArea rows={3} placeholder={t('products.form.featuresPlaceholder')} />
          </Form.Item>

          <Form.Item name="thumbnail" label={t('products.form.thumbnail')}>
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>

          <Form.Item name="source_url" label={t('products.form.sourceUrl')}>
            <Input placeholder="https://example.com/product" />
          </Form.Item>

          <Divider orientation="left" plain>{t('products.form.contactInfo')}</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="contact_person" label={t('products.form.contactPerson')}>
                <Input placeholder={t('products.form.contactPersonPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contact_email" label={t('products.form.email')}>
                <Input placeholder="email@example.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contact_phone" label={t('products.form.phone')}>
                <Input placeholder="02-1234-5678" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain>{t('products.form.statusSection')}</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="is_active" label={t('products.form.activeLabel')} valuePropName="checked">
                <Switch checkedChildren={t('products.form.activeOn')} unCheckedChildren={t('products.form.activeOff')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_featured" label={t('products.form.featuredLabel')} valuePropName="checked">
                <Switch checkedChildren={t('products.form.featuredOn')} unCheckedChildren={t('products.form.featuredOff')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}

export default ProductListPage;
